import {
  type BrowserCommand,
  type BrowserSessionSnapshot,
  type BrowserTabSnapshot,
  type EnvironmentApi,
  type EnvironmentId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { type MutableRefObject, useCallback } from "react";
import { useBrowserStateStore } from "~/browserStateStore";
import { ensureEnvironmentApi } from "~/environmentApi";
import { toastManager } from "./ui/toast";
import { resolveBrowserRestartUrl, withBrowserOperationTimeout } from "./BrowserPreviewPanel.logic";

const BROWSER_SESSION_CLOSE_TIMEOUT_MS = 12_000;

export type BrowserOptionsAction = "restart" | "close" | "clear-data" | "console-logs";

type BrowserApi = NonNullable<EnvironmentApi["browser"]>;

interface BrowserSessionActionOptions {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabUrl: string | null;
  readonly environmentId: EnvironmentId;
  readonly handleBrowserError: (error: unknown) => void;
  readonly invalidateInFlightBrowserCommands: () => void;
  readonly lifecycleAction: BrowserOptionsAction | null;
  readonly readBrowserAddressValue: (override?: string | null) => string;
  readonly runCommand: (command: BrowserCommand) => Promise<boolean>;
  readonly session: BrowserSessionSnapshot | null;
  readonly sessionOpenInitialUrlRef: MutableRefObject<string>;
  readonly setBrowserRuntimeError: (value: string | null) => void;
  readonly setLifecycleAction: (value: BrowserOptionsAction | null) => void;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

interface BrowserSessionActions {
  readonly handleBrowserOptionsAction: (action: BrowserOptionsAction) => Promise<void>;
}

export function useBrowserSessionActions({
  activeTab,
  activeTabUrl,
  environmentId,
  handleBrowserError,
  invalidateInFlightBrowserCommands,
  lifecycleAction,
  readBrowserAddressValue,
  runCommand,
  session,
  sessionOpenInitialUrlRef,
  setBrowserRuntimeError,
  setLifecycleAction,
  setSessionSnapshot,
  threadId,
  threadRef,
}: BrowserSessionActionOptions): BrowserSessionActions {
  const closeBrowserSession = useCallback(async (): Promise<boolean> => {
    if (!session) {
      return true;
    }
    const api = ensureEnvironmentApi(environmentId);
    if (!api.browser) {
      handleBrowserError("Browser runtime is not available for this environment.");
      return false;
    }
    await withBrowserOperationTimeout(
      "Browser session close",
      api.browser.closeSession({
        threadId,
        environmentId,
        browserId: session.browserId,
      }),
      BROWSER_SESSION_CLOSE_TIMEOUT_MS,
    );
    useBrowserStateStore.getState().clearBrowserState(threadRef);
    setBrowserRuntimeError(null);
    return true;
  }, [environmentId, handleBrowserError, session, setBrowserRuntimeError, threadId, threadRef]);

  const handleCloseBrowserOption = useCallback(async () => {
    const closed = await closeBrowserSession();
    if (closed) {
      toastManager.add({ type: "success", title: "Browser session closed" });
    }
  }, [closeBrowserSession]);

  const handleClearBrowserDataOption = useCallback(
    async (browserApi: BrowserApi) => {
      await browserApi.clearBrowsingData({ environmentId });
      useBrowserStateStore.getState().clearBrowserState(threadRef);
      toastManager.add({ type: "success", title: "Browser data cleared" });
    },
    [environmentId, threadRef],
  );

  const handleConsoleLogsOption = useCallback(async () => {
    if (!activeTab) {
      return;
    }
    await runCommand({ type: "consoleLogs", tabId: activeTab.tabId });
    toastManager.add({ type: "success", title: "Console logs requested" });
  }, [activeTab, runCommand]);

  const handleRestartBrowserOption = useCallback(
    async (browserApi: BrowserApi) => {
      const restartUrl = resolveBrowserRestartUrl({
        activeTabUrl,
        addressValue: readBrowserAddressValue(),
        fallbackUrl: sessionOpenInitialUrlRef.current,
      });
      const closed = await closeBrowserSession();
      if (!closed) {
        return;
      }
      const opened = await browserApi.openSession({
        threadId,
        environmentId,
        initialUrl: restartUrl,
      });
      setSessionSnapshot(threadRef, opened);
      toastManager.add({ type: "success", title: "Browser restarted" });
    },
    [
      activeTabUrl,
      closeBrowserSession,
      environmentId,
      readBrowserAddressValue,
      sessionOpenInitialUrlRef,
      setSessionSnapshot,
      threadId,
      threadRef,
    ],
  );

  const runBrowserOptionsAction = useCallback(
    async (action: BrowserOptionsAction, browserApi: BrowserApi) => {
      switch (action) {
        case "close":
          await handleCloseBrowserOption();
          return;
        case "clear-data":
          await handleClearBrowserDataOption(browserApi);
          return;
        case "console-logs":
          await handleConsoleLogsOption();
          return;
        case "restart":
          await handleRestartBrowserOption(browserApi);
          return;
      }
    },
    [
      handleClearBrowserDataOption,
      handleCloseBrowserOption,
      handleConsoleLogsOption,
      handleRestartBrowserOption,
    ],
  );

  const handleBrowserOptionsAction = useCallback(
    async (action: BrowserOptionsAction) => {
      if (lifecycleAction) {
        return;
      }
      const api = ensureEnvironmentApi(environmentId);
      if (!api.browser) {
        handleBrowserError("Browser runtime is not available for this environment.");
        return;
      }
      if (action === "restart" || action === "close" || action === "clear-data") {
        invalidateInFlightBrowserCommands();
      }
      setLifecycleAction(action);
      setBrowserRuntimeError(null);
      try {
        await runBrowserOptionsAction(action, api.browser);
      } catch (error) {
        handleBrowserError(error);
      } finally {
        setLifecycleAction(null);
      }
    },
    [
      environmentId,
      handleBrowserError,
      invalidateInFlightBrowserCommands,
      lifecycleAction,
      runBrowserOptionsAction,
      setBrowserRuntimeError,
      setLifecycleAction,
    ],
  );

  return { handleBrowserOptionsAction };
}
